import React, { useEffect, useRef, useState } from 'react'
import { Stack, Typography, useTheme } from '@mui/material'
import { useReactToPrint } from 'react-to-print'
import html2pdf from 'html2pdf.js'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { FileOpener, FileOpenerOptions } from '@capacitor-community/file-opener'

import TestIds from '../../testIds'
import { PageContainer } from '../../components/layout/PageContainer'
import TitleBox from '../../components/TitleBox'
import { CONTENT_LAYOUT_WIDTH } from '../../util/consts'
import Section from '../../components/layout/Section'
import { Button } from '../../components/Button'
import SeedEditPriceProvider from './components/useSeedEditPriceModal'
import { SeedOrderSummaryDetails, SeedOrderSummaryForm } from './form/SeedOrderSummaryForm'
import { BackButton } from '../../components/BackButton'
import { Loader } from '../../components/Loader'

export const SeedOrderSummary = () => {
  const componentRef = useRef(null)
  const navigate = useNavigate()

  const [printLoading, setPrintLoading] = useState(false)

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    print: async (printIframe: HTMLIFrameElement) => {
      setPrintLoading(true)
      const content = printIframe.contentDocument?.firstElementChild
      const filename = 'FarmlandsPRO_seed_order_summary.pdf'
      const opts = { margin: 10, filename, html2canvas: { scale: 2 } }

      if (Capacitor.isNativePlatform()) {
        const writeCapacitorFile = async (pdf: string) => {
          await Filesystem.writeFile({
            path: filename,
            data: btoa(pdf),
            directory: Directory.Documents,
          })
            .then((result) => {
              const fileOpenerOptions: FileOpenerOptions = {
                filePath: result.uri,
                contentType: 'application/pdf',
                openWithDefault: true,
              }
              FileOpener.open(fileOpenerOptions)
              setPrintLoading(false)
              return true
            })
            .catch((error) => {
              setPrintLoading(false)
              return error
            })
        }

        html2pdf()
          .set(opts)
          .from(content)
          .outputPdf()
          .then((pdf: string) => {
            Filesystem.requestPermissions()
              .then((permission) => {
                if (permission) {
                  writeCapacitorFile(pdf)
                }
              })
              .catch((e) => {
                setPrintLoading(false)
                console.error(e)
              })
          })
      } else {
        html2pdf().set(opts).from(content).save()
        setPrintLoading(false)
      }
    },
  })

  const [generatedTime, setGeneratedTime] = useState('')

  useEffect(() => {
    const date = dayjs()
    setGeneratedTime(`Generated on ${date.format('DD/MM/YY')} at ${date.format('hh:mm a')}`)
  }, [])

  const theme = useTheme()

  return (
    <PageContainer
      data-testid={TestIds.pages.seedOrderSummary}
      sx={{ maxWidth: `${CONTENT_LAYOUT_WIDTH}px` }}
    >
      <SeedEditPriceProvider>
        <BackButton text='Seed' onClick={() => navigate('/seed')} />
        <div ref={componentRef}>
          <Section pb={3}>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              flexWrap='wrap'
              gap={1}
            >
              <Stack>
                <TitleBox display='Order Summary' />
                <Typography variant='body2' color={theme.colors.primary500} mt={1}>
                  {generatedTime}
                </Typography>
              </Stack>
              {printLoading ? (
                <Loader title='Printing' type='inline-flex' />
              ) : (
                <Button
                  variant='contained'
                  onClick={handlePrint}
                  disabled={printLoading}
                  data-html2canvas-ignore={true}
                >
                  Print orders
                </Button>
              )}
            </Stack>
          </Section>
          <SeedOrderSummaryDetails />
        </div>
        <SeedOrderSummaryForm />
      </SeedEditPriceProvider>
    </PageContainer>
  )
}
